import { memo, useState, type KeyboardEvent } from 'react';
import { approvalMode, isReviewable, previewLinks, type CiRun, type Task } from '@shared/domain.ts';
import { headline, previewDeploy, primaryAction, agentPulse } from '@/data/selectors.ts';
import { ago, cn, plural } from '@/lib/format.ts';
import { AgentPulseChip, CiPulseChip, DeployPulseChip, PrChip } from './Pulses.tsx';
import { Shot } from './Shot.tsx';
import { Lightbox } from './Lightbox.tsx';
import { api } from '@/lib/api.ts';
import { Tip } from './ui.tsx';
import { listRowClass } from './Board.tsx';
import { KindPill } from './TaskFacts.tsx';

export interface RowContext {
  now: number;
  isAdmin: boolean;
  selected: boolean;
  /** Where it came from, in words: "from Kyrre", "Brigg #412" — see `describeOrigin`. */
  originLabel: string;
  agentName: string | null;
  /** Which project this task belongs to — only set on the "All projects" board. */
  projectLabel: string | null;
  categoryLabel: string | null;
  repoLabel: string | null;
  commentCount: number;
  showThumbnails: boolean;
  ciRuns: Record<string, CiRun>;
  previewWorkflow: string | null;
  draft: string;
  onDraft: (value: string) => void;
  onComment: () => void;
  onSelect: () => void;
  onApprove: () => void;
  onSignOff: () => void;
  onRetry: () => void;
}

/**
 * One task, on the pale card.
 *
 * The row separates **links from actions**: things you can open on the left of
 * the divider, the one thing you can *do* on the right. There is no "ready to
 * review" badge — the preview link says that by existing, and a badge that
 * repeats it is a badge that can be wrong.
 */
export const TaskRow = memo(function TaskRow({ task, ctx }: { task: Task; ctx: RowContext }) {
  const [hovered, setHovered] = useState(false);
  const [shotAt, setShotAt] = useState<number | null>(null);
  const active = hovered || ctx.selected;
  const pulse = agentPulse(task, ctx.now);
  const line = headline(task, pulse);
  const primary = primaryAction(task, ctx.isAdmin);
  const previews = previewLinks(task);
  const session = task.links.find((link) => link.kind === 'session');
  const deploy = task.links.find((link) => link.kind === 'deploy');
  const preview = previewDeploy(task, ctx.ciRuns, ctx.previewWorkflow);

  const pill = pillFor(task, ctx.isAdmin);
  const hasImages = ctx.showThumbnails && task.images.length > 0;
  // Same reasoning as the detail pane: a routine appends as it goes, so the
  // most recent screenshot is last in the array and first on screen.
  const shotsLatestFirst = hasImages ? [...task.images].reverse() : task.images;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-current={ctx.selected}
      onClick={ctx.onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          ctx.onSelect();
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={listRowClass(ctx.selected)}
    >
      {/* title line */}
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 sm:gap-x-2.5 sm:gap-y-1.5">
        <span className="font-mono text-[11.5px] text-carddim tabular-nums">#{task.ref}</span>
        <KindPill kind={task.kind} />
        <span className="min-w-[140px] flex-1 truncate text-[13.5px] font-semibold text-cardhi sm:min-w-[170px] sm:text-[14.5px]">
          {task.title}
        </span>

        {pill ? (
          <span
            className={cn(
              'shrink-0 rounded-full px-2.5 py-[3px] text-[11.5px] font-semibold whitespace-nowrap',
              pill.className,
            )}
          >
            {pill.text}
          </span>
        ) : null}

        {/*
          `min-w-0`, not `shrink-0`: this group can run wide enough on a
          phone — GitHub PR, preview, session, plus a divider and an action
          button — to overflow the card even alone on its own wrapped line.
          `shrink-0` refused to let that width give at all, which pushed the
          action button (the one thing here you can actually do something
          with) half off the edge instead of onto a second line.
        */}
        <span className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-1.5">
          {/*
            Quiet at rest, not hidden. Hiding them still reserved the space, so
            a row's pill and its action sat either side of a gap with nothing in
            it — which is what made them look like they were floating. Dimming
            keeps the board calm without the hole, and without the layout moving
            under the cursor.
          */}
          <span
            className={cn(
              'flex flex-wrap items-center gap-1.5 transition-opacity duration-100',
              active ? 'opacity-100' : 'opacity-55',
            )}
          >
            <PrChip task={task} />
            {previews.map((link) => (
              <RowLink key={link.url} href={link.url} label={link.label} />
            ))}
            {deploy ? <RowLink href={deploy.url} label={deploy.label} /> : null}
            {session ? <RowLink href={session.url} label="Session" /> : null}
          </span>

          {primary ? (
            <>
              <span className="h-4 w-px bg-cardline" aria-hidden />
              <RowAction
                primary={primary}
                early={approvalMode(task) === 'auto_merge' && !task.merge_held_by}
                onApprove={ctx.onApprove}
                onSignOff={ctx.onSignOff}
                onRetry={ctx.onRetry}
              />
            </>
          ) : null}
        </span>
      </div>

      {/* what the routine last said */}
      {line ? (
        <div className="truncate text-[12.5px] leading-relaxed text-cardbody sm:text-[13px]">{line}</div>
      ) : null}

      {/* the two states, plus who and when */}
      <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] text-carddim sm:gap-x-2 sm:gap-y-1 sm:text-[12.5px]">
        <AgentPulseChip task={task} now={ctx.now} showDetail={!line} />
        <CiPulseChip task={task} now={ctx.now} />
        {/*
          A running preview deploy is still "GitHub working on the branch",
          but it is not the same claim as a check running — someone reading
          "building" here would reasonably expect tests, not a live rollout.
          Named separately, right next to it, rather than folded into the
          same word. Gone once the work has landed: the preview is of a branch
          that no longer matters, and the badge already says where it went.
        */}
        {preview.state !== 'none' && task.status !== 'merged' && task.status !== 'deployed' ? (
          <DeployPulseChip deploy={preview} now={ctx.now} />
        ) : null}
        {ctx.projectLabel ? (
          <>
            <Dot />
            <span className="font-medium text-cardmid">{ctx.projectLabel}</span>
          </>
        ) : null}
        <Dot />
        <span className="font-medium text-cardmid">{ctx.originLabel}</span>
        {/* The routine on it now — said once: a routine's own task already names it. */}
        {ctx.agentName && !(task.origin === 'routine' && task.origin_ref === ctx.agentName) ? (
          <>
            <Dot />
            <span>{ctx.agentName}</span>
          </>
        ) : null}
        {ctx.repoLabel ? (
          <>
            <Dot />
            <span className="font-mono text-[11.5px]">{ctx.repoLabel}</span>
          </>
        ) : null}
        {ctx.categoryLabel ? (
          <>
            <Dot />
            <span>{ctx.categoryLabel}</span>
          </>
        ) : null}
        {/*
          Down here with the other things that are simply true of the task —
          who raised it, which repo, which category — rather than up in the
          title line with the status pills. Auto-merge is a setting, not a
          state: it says nothing about where the work has got to, and in the
          title line it was the pill most likely to be the only one there,
          floating between the title and the actions.
        */}
        {task.auto_merge ? (
          <>
            <Dot />
            {/*
              Held: the pull request touches a path the repository protects,
              which the flag never waives — so this one waits for a person.
            */}
            {task.merge_held_by ? (
              <Tip content={task.merge_held_by}>
                <span className="font-medium text-amberx">Auto-merge held</span>
              </Tip>
            ) : (
              <span className="font-medium text-violet">Auto-merge</span>
            )}
          </>
        ) : null}
        {task.smart ? (
          <>
            <Dot />
            <span className="font-medium text-violet">Smart</span>
          </>
        ) : null}
        {/*
          Landed with nobody's yes: the project's waiver check passed at the
          head. Said on the card, and named, because "who approved this?" is
          the first question anyone asks of a merge they did not expect.
        */}
        {task.merge_waived_by ? (
          <>
            <Dot />
            <Tip content={`Merged without approval — “${task.merge_waived_by}” passed at the head`}>
              <span className="font-medium text-violet">Waived by {task.merge_waived_by}</span>
            </Tip>
          </>
        ) : null}
        <Dot />
        <span>{ago(task.updated_at, ctx.now)}</span>
        {ctx.commentCount > 0 ? (
          <>
            <Dot />
            <span className="font-medium text-cardmid">
              {plural(ctx.commentCount, 'comment')}
            </span>
          </>
        ) : null}
      </div>

      {shotAt !== null ? (
        <div onClick={(event) => event.stopPropagation()}>
          <Lightbox
            images={shotsLatestFirst}
            index={shotAt}
            onIndex={setShotAt}
            onClose={() => setShotAt(null)}
            onComment={(body) => api.comment(task.id, body)}
            projectId={task.project_id}
          />
        </div>
      ) : null}

      {/*
        Screenshots and the inline comment box. The comment box only ever
        shows on hover or when selected, and a selected row on a phone is
        swapped out for the detail pane rather than shown inline — so with
        no images either, this row has nothing to say on mobile and costs
        nothing to skip.
      */}
      <div
        className={cn(
          'mt-[3px] flex items-end justify-between gap-2.5',
          hasImages ? 'min-h-[22px]' : 'hidden min-h-0 sm:flex sm:min-h-[22px]',
        )}
      >
        {hasImages ? (
          <div className="flex gap-1.5">
            {shotsLatestFirst.slice(0, 4).map((image, index) => (
              <Tip key={image.url} content={image.label ?? 'Screenshot'}>
                <button
                  type="button"
                  // The row itself opens the task; a thumbnail should not.
                  onClick={(event) => {
                    event.stopPropagation();
                    setShotAt(index);
                  }}
                  aria-label={`Open ${image.label ?? 'screenshot'} full size`}
                  className="rg-focus block cursor-zoom-in"
                >
                  <Shot url={image.url} label={image.label} className="h-10 w-[62px]" />
                </button>
              </Tip>
            ))}
            {task.images.length > 4 ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setShotAt(4);
                }}
                className="rg-focus cursor-zoom-in self-center pl-1 text-[11.5px] text-carddim hover:text-cardhi"
              >
                +{task.images.length - 4}
              </button>
            ) : null}
          </div>
        ) : (
          <span />
        )}

        {/*
          Type and press Enter — no need to open the task. The draft is held by
          the list, not by this input, so a routine reporting in mid-sentence
          cannot wipe what you were typing.
        */}
        <input
          value={ctx.draft}
          onChange={(event) => ctx.onDraft(event.target.value)}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
            event.stopPropagation();
            if (event.key === 'Enter') {
              event.preventDefault();
              ctx.onComment();
            }
            if (event.key === 'Escape') event.currentTarget.blur();
          }}
          placeholder="Message the routine…"
          aria-label={`Comment on ${task.title}`}
          className={cn(
            'ml-auto h-7 w-[180px] max-w-[46%] shrink-0 rounded-[7px] border border-cardline bg-[rgba(26,29,49,0.05)] sm:h-[22px]',
            // 16px below `sm`, not the desktop 12px — under 16px, iOS Safari
            // zooms the page in on focus.
            'px-2.5 text-base leading-7 text-cardhi outline-none transition-opacity duration-100 sm:text-[12px] sm:leading-5',
            'placeholder:text-carddim focus:border-accent focus:opacity-100 focus:shadow-[0_0_0_3px_var(--accent-soft)]',
            active || ctx.draft ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
        />
      </div>
    </div>
  );
});

const Dot = () => <span aria-hidden>·</span>;

export function RowLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={(event) => event.stopPropagation()}
      className="rg-focus rounded-full border border-cardbtnbd bg-cardbtn px-3 py-1 text-[12px] font-semibold whitespace-nowrap text-cardbtntx no-underline hover:bg-cardbtnhover hover:text-cardhi hover:no-underline"
    >
      {label}
    </a>
  );
}

export function RowAction({
  primary,
  early,
  onApprove,
  onSignOff,
  onRetry,
}: {
  primary: 'approve' | 'sign-off' | 'retry' | 'send-back';
  /** Still in progress: approving turns auto-merge on rather than landing it now. */
  early: boolean;
  onApprove: () => void;
  onSignOff: () => void;
  onRetry: () => void;
}) {
  const stop = (fn: () => void) => (event: React.MouseEvent) => {
    event.stopPropagation();
    fn();
  };

  if (primary === 'retry') {
    return (
      <button
        onClick={stop(onRetry)}
        className="rg-focus cursor-pointer rounded-full border border-warn/30 bg-warn/10 px-3 py-1 text-[12px] font-semibold whitespace-nowrap text-warn hover:bg-warn/20"
      >
        Retry
      </button>
    );
  }
  if (primary === 'approve') {
    return (
      <button
        onClick={stop(onApprove)}
        title={early ? 'Still in progress — approving turns auto-merge on, and Rigg lands it once it is done, green and reviewed' : undefined}
        // `accent` is the dark-shell mint; on the pale row it is a bright wash
        // with almost nothing under it, and worse on a selected row, which is
        // already tinted the same hue. `good` is the card-side member of that
        // family and carries white.
        className="rg-focus cursor-pointer rounded-full bg-good px-3.5 py-1 text-[12px] font-[650] whitespace-nowrap text-white hover:brightness-110"
      >
        {early ? 'Auto-merge' : 'Approve'}
      </button>
    );
  }
  return (
    <button
      onClick={stop(onSignOff)}
      className="rg-focus cursor-pointer rounded-full border border-good/30 bg-good/10 px-3 py-1 text-[12px] font-semibold whitespace-nowrap text-good hover:bg-good/20"
    >
      Sign off
    </button>
  );
}

/**
 * At most one pill, and only when it says something the two state chips do not.
 * "Ready to review" is not a pill: the preview link is the claim.
 */
function pillFor(task: Task, isAdmin: boolean): { text: string; className: string } | null {
  // Outranks everything below: a person put the task here on purpose, which
  // is a more current fact than whatever the ladder or the routine's own
  // state says.
  if (task.deleted_at) return { text: 'Trashed', className: 'bg-warn/12 text-warn' };
  if (task.snoozed_at) return { text: 'Snoozed', className: 'bg-[rgba(91,97,120,0.14)] text-[#5b6178]' };
  // Outranks everything below: whatever the ladder or the routine's own state
  // says, the pull request closing without merging is the more current fact —
  // there is nothing left here for anyone to act on.
  if (task.archived_at) return { text: 'Archived', className: 'bg-[rgba(91,97,120,0.14)] text-[#5b6178]' };
  // A fire-and-forget handoff the routine accepted is finished, but nothing
  // Rigg shipped is live — the routine was sent it, and that is all anyone knows.
  if (task.status === 'deployed' && task.source === 'background') {
    return { text: 'Sent', className: 'bg-[rgba(91,97,120,0.14)] text-[#5b6178]' };
  }
  if (task.status === 'deployed') return { text: 'Live', className: 'bg-good/13 text-good' };
  if (task.status === 'merged') return { text: 'Merged', className: 'bg-[rgba(91,97,120,0.14)] text-[#5b6178]' };
  if (task.status === 'approved') {
    // Approval answers "may this land?", not whatever the routine is asking
    // now. A bare "Approved ✓" over a task that is parked on you reads as
    // "nothing to do here" on the one row where the opposite is true.
    if (task.agent_state === 'waiting_on_you') {
      return { text: 'Approved · waiting on you', className: 'bg-amber/14 text-amber' };
    }
    if (task.agent_state === 'blocked') {
      return { text: 'Approved · blocked', className: 'bg-warn/12 text-warn' };
    }
    return { text: 'Approved ✓', className: 'bg-good/13 text-good' };
  }
  if (task.status === 'signed_off') {
    return isAdmin
      ? { text: 'Needs your approval', className: 'bg-good/13 text-good' }
      : { text: 'Signed off', className: 'bg-good/13 text-good' };
  }
  if (task.status === 'error') return { text: 'Failed', className: 'bg-warn/12 text-warn' };
  if (task.agent_state === 'waiting_on_you') {
    return { text: 'Waiting on you', className: 'bg-amber/14 text-amber' };
  }
  // Only once there is something to open. Badging "Ready for review" over a
  // task whose preview is still deploying is the same false alarm the state was
  // introduced to stop, just moved from the queue onto the card.
  if (task.agent_state === 'ready_for_review' && isReviewable(task)) {
    return { text: 'Ready for review', className: 'bg-amber/14 text-amber' };
  }
  if (isReviewable(task) && !task.first_preview_at) return null;
  return null;
}
